import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  AP_INVOICE_SNOOZE_PRESETS,
  ExtensionColumnSelectionsSchema,
  ExtensionColumnsMetadataSchema,
  ExtensionValueSchema,
  ApExceptionRulesSchema,
  TroubleDataSchema,
} from "@erp/shared-types";

/**
 * Scalar helpers
 */
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

export const APInvoiceGLAccountSummarySchema = z.object({
  id: UUIDSchema,
  accountCode: z.string(),
  accountName: z.string(),
  accountDescription: z.string().nullable().optional(),
  accountType: z.string().nullable().optional(),
  isControlAccount: z.boolean(),
});

export const APInvoiceSupplierGLAccountsSchema = z.object({
  expense: APInvoiceGLAccountSummarySchema.nullable().optional(),
  ap: APInvoiceGLAccountSummarySchema.nullable().optional(),
  po: APInvoiceGLAccountSummarySchema.nullable().optional(),
});

export const APInvoiceSupplierSummarySchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  contactName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  paymentTerms: z.string().nullable().optional(),
  status: z.string(),
  notes: z.array(z.string()).nullable().optional(),
  defaultPaymentMethod: z.string().nullable().optional(),
  glAccounts: APInvoiceSupplierGLAccountsSchema.nullable().optional(),
});

/**
 * Pagination schema mirroring common backend pattern
 */
export const APInvoicePaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
  // Set when excludeSnoozed hid rows: how many matching invoices are still snoozed.
  snoozedCount: z.int().nonnegative().optional(),
});

/**
 * Cap for `invoiceIds` on the list endpoint and on shared invoice-list URLs.
 * Keeps a shared link under typical proxy URL limits (100 UUIDs is ~5KB encoded).
 */
export const MAX_SHARED_AP_INVOICE_IDS = 100;

/**
 * Cap for `supplierIds` on the invoice list. Keeps a multi-vendor filter
 * under typical proxy URL limits.
 */
export const MAX_AP_INVOICE_SUPPLIER_FILTER_IDS = 50;

/**
 * Filters accepted by the list endpoint
 * All fields optional; server treats undefined as no filter
 */
export const ListAPInvoicesInputSchema = z.object({
  extensionColumns: ExtensionColumnSelectionsSchema.max(100).optional(),
  invoiceDateFrom: z.string().optional(),
  invoiceDateTo: z.string().optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  /** Inclusive UTC instant; client sends local start-of-day. */
  receivedAtFrom: z.iso.datetime().optional(),
  /** Inclusive UTC instant; client sends local end-of-day. */
  receivedAtTo: z.iso.datetime().optional(),
  status: z.array(z.string()).optional(),
  paymentStatus: z.array(z.string()).optional(),
  /** Matches the invoice list warning: active issues, payment or linked-document holds. */
  trouble: z.enum(["trouble", "no_trouble"]).optional(),
  /**
   * The viewer's local date. The `overdue` payment status means due before
   * this day. Defaults to the UTC date.
   */
  overdueAsOf: z.iso.date().optional(),
  supplierId: UUIDSchema.optional(),
  /**
   * Invoices for any of these suppliers. Combined with `supplierId` when
   * both are set. The invoices page sends this from the vendor filter.
   */
  supplierIds: z
    .array(UUIDSchema)
    .min(1)
    .max(MAX_AP_INVOICE_SUPPLIER_FILTER_IDS)
    .optional(),
  invoiceNumber: z.string().optional(),
  /**
   * Restrict the list to these invoices (shared links). When set without
   * `status`, ignored invoices that are in the list are included.
   */
  invoiceIds: z
    .array(UUIDSchema)
    .min(1)
    .max(MAX_SHARED_AP_INVOICE_IDS)
    .optional(),
  referenceId: z.string().optional(),
  minTotalAmount: DecimalStringSchema.optional(),
  maxTotalAmount: DecimalStringSchema.optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
  includeNumLineItemsMatched: z.boolean().optional(),
  // The invoices list sets this to hide invoices whose snooze is still in
  // the future. Other callers leave it unset and still receive those invoices.
  excludeSnoozed: z.boolean().optional(),
});

/**
 * Base invoice schema mirroring Sequelize attributes (sans line items)
 */
export const APInvoiceHoldOriginSchema = z.enum([
  "PURCHASE_ORDER_LINE",
  "SALES_ORDER_LINE",
  "SALES_LOAD_CHARGE",
  "CHARGE",
]);

/** One active source-ERP hold on a record of a document linked to the invoice. */
export const APInvoiceHoldSchema = z.object({
  /** Famous hold kind: HOLD_PAY, LOAD_HOLD or CONSIGN. */
  value: z.string(),
  origin: APInvoiceHoldOriginSchema,
  recordId: UUIDSchema,
  documentType: z.string(),
  documentId: UUIDSchema,
  documentNumber: z.string().nullable(),
  lineNumber: z.int().nullable(),
  /** The held record is matched to one of the invoice's line items. */
  matched: z.boolean(),
});

export const APInvoiceSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  supplierId: UUIDSchema.nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  /** @deprecated Use referenceIds instead */
  referenceId: z.string().nullable().optional(),
  referenceIds: z.array(z.string()).nullable().optional(),
  invoiceDate: z.string(),
  receivedAt: z.string().nullable().optional(),
  dueDate: z.string(),
  subtotal: DecimalStringSchema,
  taxAmount: DecimalStringSchema.nullable().optional(),
  totalAmount: DecimalStringSchema,
  currency: z.string(),
  paymentTerms: z.string().nullable().optional(),
  paymentStatus: z.string(),
  paidAt: z.string().nullable().optional(),
  paidAmount: DecimalStringSchema.nullable().optional(),
  preferredPayDate: z.string().nullable().optional(),
  scheduledPaymentDate: z.string().nullable().optional(),
  scheduledPaymentExplanation: z.string().nullable().optional(),
  approvedAt: z.string().nullable().optional(),
  approvedByUserId: UUIDSchema.nullable().optional(),
  isVoided: z.boolean(),
  voidedAt: z.string().nullable().optional(),
  voidedByUserId: UUIDSchema.nullable().optional(),
  voidReason: z.string().nullable().optional(),
  paymentHold: z.boolean(),
  paymentHoldReason: z.string().nullable().optional(),
  paymentHoldByUserId: UUIDSchema.nullable().optional(),
  paymentHoldAt: z.string().nullable().optional(),
  // Non-status "flagged for a reason" indicators (e.g. matching paused while a
  // matched order isn't yet in a status that allows matching). Orthogonal to
  // `status`.
  troubleData: TroubleDataSchema,
  status: z.string(),
  source: z.string().nullable().optional(),
  sourceId: UUIDSchema.nullable().optional(),
  glAccountId: UUIDSchema.nullable().optional(),
  costCenterId: UUIDSchema.nullable().optional(),
  departmentId: UUIDSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
  originSyncStatus: z.string().nullable().optional(),
  originSyncErrorMessage: z.string().nullable().optional(),
  originMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  snoozedUntil: z.coerce.date().nullable().optional(),
  snoozedByUserId: UUIDSchema.nullable().optional(),
  snoozeClearsOnEdit: z.boolean().optional(),
  snoozeNote: z.string().nullable().optional(),
  snoozePreset: z.enum(AP_INVOICE_SNOOZE_PRESETS).nullable().optional(),
  supplier: APInvoiceSupplierSummarySchema.nullable().optional(),
  glAccount: APInvoiceGLAccountSummarySchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  lineItemMatchSummary: z
    .object({
      matched: z.int().nonnegative(),
      confirmed: z.int().nonnegative(),
      tentative: z.int().nonnegative(),
      unmatched: z.int().nonnegative(),
      total: z.int().nonnegative(),
    })
    .nullable()
    .optional(),
  /**
   * Source-ERP holds on the documents linked to the invoice (list only, same
   * source as `listHolds`). Orthogonal to the invoice-level `paymentHold`,
   * which is set by our users.
   */
  sourceHolds: z.array(APInvoiceHoldSchema).optional(),
  /** Notes (comments) left on the invoice (list only). */
  noteCount: z.int().nonnegative().optional(),
});

export const ListAPInvoicesResponseSchema = z.object({
  invoices: z.array(
    APInvoiceSchema.extend({
      ext: z.record(z.string(), ExtensionValueSchema).optional(),
    }),
  ),
  extensionColumns: ExtensionColumnsMetadataSchema.optional(),
  pagination: APInvoicePaginationSchema,
});

export const GetAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const GetAPInvoiceExtractionResultInputSchema = z.object({
  id: UUIDSchema,
});

export const APInvoiceExtractionResultSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  apInvoiceId: UUIDSchema.nullable(),
  commInboundEmailId: UUIDSchema.nullable(),
  documentId: UUIDSchema.nullable(),
  modelUsed: z.string(),
  status: z.enum(["success", "failed"]),
  extractedData: z.unknown().nullable(),
  rawOutput: z.unknown().nullable(),
  errorDetails: z.unknown().nullable(),
  isSelected: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const GetAPInvoiceExtractionResultResponseSchema = z.object({
  extractionResult: APInvoiceExtractionResultSchema.nullable(),
});

/**
 * Provenance of a line created by `splitLineItem`. Every part of one split
 * shares `sourceLineItemId` and the original line's figures, including
 * parts that were split again, so the parts can be shown as one group
 * summing to the original invoice line.
 */
export const APInvoiceLineItemSplitSchema = z.object({
  sourceLineItemId: UUIDSchema,
  originalLineNumber: z.int().nullable(),
  originalQuantity: DecimalStringSchema,
  originalUnitPrice: DecimalStringSchema,
  originalTotalPrice: DecimalStringSchema,
});

/**
 * A suggested split of an invoice line across Famous charges (GEN-3198). Each
 * part is an allocation: one amount backed by the charge on every file that
 * belongs to it (the load's, the order's credit, the PO's). A `proposed`
 * record is already linked to its charges as tentative matches; an
 * `ambiguous` one only lists the alternatives and links nothing.
 */
export const APInvoiceLineItemSplitProposalMemberSchema = z.object({
  chargeId: UUIDSchema,
  source: z.enum(["LOAD", "PO", "ORDER"]),
  docId: UUIDSchema,
  docNumber: z.string().nullable(),
  /** The charge as the source ERP holds it, signed. */
  amount: DecimalStringSchema,
  role: z.enum(["primary", "companion"]),
});

export const APInvoiceLineItemSplitProposalAllocationSchema = z.object({
  kind: z.enum(["LOAD", "PO", "PICKUP"]),
  /** What this part bills. */
  amount: DecimalStringSchema,
  members: z.array(APInvoiceLineItemSplitProposalMemberSchema),
});

export const APInvoiceLineItemSplitProposalSchema = z.object({
  status: z.enum(["proposed", "ambiguous"]),
  groupId: z.string().nullable(),
  /** The line total the proposal was computed for. */
  lineAmount: DecimalStringSchema,
  parts: z.array(APInvoiceLineItemSplitProposalAllocationSchema).optional(),
  alternatives: z
    .array(z.array(APInvoiceLineItemSplitProposalAllocationSchema))
    .optional(),
  confidence: z.number().nullable(),
  reasoning: z.string(),
});

export const APInvoiceLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int(),
  description: z.string().nullable().optional(),
  quantity: z.union([z.number(), z.string()]).nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  totalPrice: DecimalStringSchema.nullable().optional(),
  taxAmount: DecimalStringSchema.nullable().optional(),
  split: APInvoiceLineItemSplitSchema.nullable().optional(),
  splitProposal: APInvoiceLineItemSplitProposalSchema.nullable().optional(),
});

export const InboundEmailAttachmentSchema = z.object({
  id: UUIDSchema,
  fileName: z.string(),
  sizeBytes: z.number().nullable().optional(),
  mimeType: z.string().nullable().optional(),
  s3Url: z.string(),
  contentId: z.string().nullable().optional(),
});

export const InboundEmailSchema = z.object({
  id: UUIDSchema,
  conversationId: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  from: z.string().nullable(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  htmlContent: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
  attachments: z.array(InboundEmailAttachmentSchema).optional(),
  receivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * Summary of an email thread that contributed to an AP invoice.
 *
 * An invoice can be touched by multiple email threads over its lifetime:
 *  - the thread it was originally created from (`isSource: true`)
 *  - any later threads whose extraction triggered an update via
 *    `arbitrateAPInvoiceUpdate`
 *
 * Threads are derived by walking `ai_ap_invoice_extraction_results.apInvoiceId`
 * back to the originating `commInboundEmailId` (or `documentId` →
 * `DocumentAssociation` → inbound email).
 */
export const APInvoiceRelatedConversationSchema = z.object({
  conversationId: z.string(),
  primaryEmailId: UUIDSchema,
  subject: z.string().nullable(),
  isSource: z.boolean(),
});

export const ExtractedTotalWarningSchema = z.object({
  /** Line extensions plus header tax, canonical 2-decimal string. */
  currentTotal: z.string(),
  /** Total read from the document, canonical 2-decimal string. */
  extractedTotal: z.string(),
  /**
   * Line extensions plus header tax as extracted. Null when those amounts
   * cannot be read.
   */
  extractedLineSum: z.string().nullable(),
  /** The extracted total does not match the extracted line items. */
  extractionDiffers: z.boolean(),
  /** The saved lines or tax are not what was extracted. */
  invoiceUpdated: z.boolean(),
});

export const GetAPInvoiceResponseSchema = z.object({
  invoice: APInvoiceSchema.nullable(),
  lineItems: z.array(APInvoiceLineItemSchema),
  inboundEmail: InboundEmailSchema.nullable(),
  relatedConversations: z.array(APInvoiceRelatedConversationSchema),
  /**
   * Set when the line-item total is not the total extracted from the document.
   * Informational only — this does not block approval.
   */
  extractedTotalWarning: ExtractedTotalWarningSchema.nullable(),
});

/**
 * Discriminates the kind of timeline event in the invoice Change History:
 * - `field_change`: a row-level INSERT/UPDATE/DELETE on the invoice or a line
 *   item (the original change-log behavior).
 * - `match`: a line item was matched/unmatched (a `core_entity_associations`
 *   row), labeled `manual` (user via API) or `auto` (workflow / AI).
 * - `workflow_run`: a workflow run (ingestion, on-approve, rerun) that
 *   processed this invoice.
 * - `scheduled_run`: a pending/snoozed rerun the invoice will execute next.
 */
export const InvoiceChangeLogKindSchema = z.enum([
  "field_change",
  "match",
  "workflow_run",
  "scheduled_run",
]);

export const InvoiceChangeLogEntrySchema = z.object({
  id: UUIDSchema,
  kind: InvoiceChangeLogKindSchema.default("field_change"),
  /**
   * Source table for `field_change`/`match` rows. `workflow_run` and
   * `scheduled_run` are synthetic (the id/entityId is the workflow run's id).
   */
  entityType: z.enum([
    "ap_invoices",
    "ap_invoice_line_items",
    "core_entity_associations",
    "workflow_run",
  ]),
  entityId: UUIDSchema,
  action: z.enum(["INSERT", "UPDATE", "DELETE"]),
  changedFields: z.record(z.string(), z.unknown()),
  actor: z.object({
    userId: UUIDSchema.nullable(),
    userName: z.string().nullable(),
    sourceType: z.enum(["api", "workflow", "system"]).nullable(),
    sourceDetail: z.string().nullable(),
  }),
  createdAt: z.coerce.date(),

  // --- `match` fields ---
  /** Whether the match was created manually (by a user) or automatically. */
  matchSource: z.enum(["manual", "auto"]).optional(),
  /** The entity the line item was matched to (PO line, charge, document, …). */
  matchTargetType: z.string().optional(),
  matchTargetId: z.string().optional(),

  // --- `workflow_run` / `scheduled_run` fields ---
  workflowRunId: UUIDSchema.optional(),
  /** Lineage id, needed to deep-link to the run-detail route. */
  workflowId: UUIDSchema.optional(),
  workflowName: z.string().nullable().optional(),
  runStatus: z.string().optional(),
  /** ISO timestamp of the next scheduled (snoozed) rerun. */
  scheduledFor: z.coerce.date().optional(),
  snoozeReason: z.string().nullable().optional(),
});

export const ListAPInvoiceChangeLogInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const ListAPInvoiceChangeLogResponseSchema = z.object({
  entries: z.array(InvoiceChangeLogEntrySchema),
});

export const CreateVoucherInFamousInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const CreateVoucherInFamousResponseSchema = z.object({
  success: z.boolean(),
  originId: z.string().optional(),
  errorMessage: z.string().optional(),
});

export const UpdateAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
  /**
   * The supplier's invoice number. Draft and matched invoices accept a
   * correction when extraction read it wrong. Null clears it; blank is
   * rejected.
   */
  invoiceNumber: z
    .string()
    .trim()
    .min(1, "Invoice number cannot be empty")
    .max(255, "Invoice number is too long")
    .nullable()
    .optional(),
  supplierId: UUIDSchema.nullable().optional(),
  /** @deprecated Use referenceIds instead */
  referenceId: z
    .string()
    .min(1, "Reference ID cannot be empty")
    .nullable()
    .optional(),
  referenceIds: z.array(z.string().min(1)).nullable().optional(),
  invoiceDate: z.string().optional(),
  dueDate: z.string().nullable().optional(),
  preferredPayDate: z.string().nullable().optional(),
  scheduledPaymentDate: z.string().nullable().optional(),
  scheduledPaymentExplanation: z.string().nullable().optional(),
});

export const UpdateAPInvoiceResponseSchema = APInvoiceSchema;

export const UpdateAPInvoiceLineItemInputSchema = z.object({
  id: UUIDSchema,
  apInvoiceId: UUIDSchema,
  quantity: DecimalStringSchema.optional(),
  unitPrice: DecimalStringSchema.optional(),
});

export const UpdateAPInvoiceLineItemResponseSchema = APInvoiceLineItemSchema;

/**
 * Split one invoice line into several lines so each can be matched to its own
 * target (e.g. one $840 lumper line → two Famous unloading charges of $415 and
 * $425). The part totals must add up to the original line total, so the
 * invoice total does not move. The first part stays on the original line.
 */
export const SplitAPInvoiceLineItemPartSchema = z.object({
  quantity: DecimalStringSchema,
  totalPrice: DecimalStringSchema,
});

export const SplitAPInvoiceLineItemInputSchema = z.object({
  apInvoiceId: UUIDSchema,
  lineItemId: UUIDSchema,
  parts: z.array(SplitAPInvoiceLineItemPartSchema).min(2).max(20),
});

export const SplitAPInvoiceLineItemResponseSchema = z.object({
  lineItems: z.array(APInvoiceLineItemSchema),
});

/**
 * Put a split invoice line back together: every part of the split the given
 * line belongs to becomes the original line again (original quantity and
 * amount). The invoice total does not move.
 */
export const UndoAPInvoiceLineItemSplitInputSchema = z.object({
  apInvoiceId: UUIDSchema,
  lineItemId: UUIDSchema.describe("Any part of the split to undo"),
});

export const UndoAPInvoiceLineItemSplitResponseSchema = z.object({
  lineItems: z.array(APInvoiceLineItemSchema),
});

/**
 * Roll several invoice lines up to ONE target — a PO/SO/TO line or a Famous
 * charge — as an aggregated match, for a bill the supplier split across lines
 * that the ERP carries as a single record (e.g. $3,850 + $150 freight lines
 * against one $4,000 freight charge). The manual counterpart of the
 * aggregation the matching step proposes on its own.
 */
export const AggregateAPInvoiceLineItemsTargetSchema = z.object({
  kind: z.enum(["PO_LINE", "SO_LINE", "TO_LINE", "CHARGE"]),
  id: UUIDSchema,
});

export const AggregateAPInvoiceLineItemsInputSchema = z.object({
  apInvoiceId: UUIDSchema,
  lineItemIds: z.array(UUIDSchema).min(2).max(20),
  target: AggregateAPInvoiceLineItemsTargetSchema,
  commonDescription: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .describe("Label shown on the aggregated subtotal row"),
});

export const AggregateAPInvoiceLineItemsResponseSchema = z.object({
  aggregationGroupId: z.string(),
  lineItemIds: z.array(UUIDSchema),
});

export const LineSplitProposalActionInputSchema = z.object({
  apInvoiceId: UUIDSchema,
  lineItemId: UUIDSchema,
});

export const LineSplitProposalActionResponseSchema = z.object({
  lineItem: APInvoiceLineItemSchema,
});

export const AcceptMatchesAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const AcceptMatchesAPInvoiceResponseSchema = APInvoiceSchema;

export const UnacceptMatchesAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const UnacceptMatchesAPInvoiceResponseSchema = APInvoiceSchema;

export const CreatePaymentAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const CreatePaymentAPInvoiceResponseSchema = APInvoiceSchema;

/**
 * How the invoice was settled outside Gently. Mirrors `PaymentMethod` on
 * `fi_payments` except the rails we don't record here.
 */
export const MarkAPInvoicePaidMethodSchema = z.enum([
  "ach",
  "wire",
  "check",
  "card",
  "cash",
  "other",
]);

export const MarkAPInvoicesPaidInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1).max(1000),
  /** Calendar date the supplier was paid (YYYY-MM-DD). */
  paidDate: z.iso.date(),
  method: MarkAPInvoicePaidMethodSchema.default("other"),
  /** Check number or bank confirmation. Stored on the payment memo. */
  referenceNumber: z.string().max(100).optional(),
  note: z.string().max(500).optional(),
});

export const MarkAPInvoicesPaidResponseSchema = z.object({
  markedPaid: z.int().nonnegative(),
  paymentIds: z.array(UUIDSchema),
  failures: z.array(
    z.object({
      invoiceId: UUIDSchema,
      error: z.string(),
    }),
  ),
});

export const BulkCreatePaymentsInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1),
  scheduledDates: z.record(z.string(), z.string()).optional(),
});

export const BulkCreatePaymentsResponseSchema = z.object({
  paymentsCreated: z.number(),
  invoicesProcessed: z.number(),
  failures: z.array(
    z.object({
      invoiceId: z.string(),
      error: z.string(),
    }),
  ),
});

/** Max pasted identifiers for the bulk payment tool lookup. */
export const MAX_AP_INVOICE_LOOKUP_IDENTIFIERS = 500;

export const LookupAPInvoicesInputSchema = z.object({
  /** Invoice numbers or invoice UUIDs, as pasted by the user. */
  identifiers: z
    .array(z.string().trim().min(1).max(255))
    .min(1)
    .max(MAX_AP_INVOICE_LOOKUP_IDENTIFIERS),
});

export const LookupAPInvoicesResponseSchema = z.object({
  /** One entry per distinct identifier, in input order. */
  results: z.array(
    z.object({
      identifier: z.string(),
      /** Empty when nothing matched; more than one when the number is shared. */
      invoices: z.array(APInvoiceSchema),
    }),
  ),
});

export const ParseCheckRunReportInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  /** The report PDF, base64 encoded (about 5 MB max). */
  base64: z.string().min(1).max(7_000_000),
});

export const CheckRunMatchMethodSchema = z.enum(["voucher", "invoice_number"]);

export const CheckRunReportLineSchema = z.object({
  vendorName: z.string(),
  invoiceNumber: z.string(),
  invoiceDate: z.iso.date(),
  voucherNumber: z.string(),
  dueDate: z.iso.date(),
  netAmount: DecimalStringSchema,
});

export const ParseCheckRunReportResponseSchema = z.object({
  runNumber: z.string().nullable(),
  /** Grand total printed on the report, when found. */
  grandTotal: DecimalStringSchema.nullable(),
  /** Sum of the parsed lines' net amounts; equals grandTotal when every row was read. */
  linesTotal: DecimalStringSchema,
  /** Rows that looked like vouchers but could not be read. */
  unparsedLines: z.array(z.string()),
  lines: z.array(
    z.object({
      line: CheckRunReportLineSchema,
      matchedBy: CheckRunMatchMethodSchema.nullable(),
      /** Empty when nothing matched; more than one when the user must pick. */
      invoices: z.array(APInvoiceSchema),
    }),
  ),
});

export const IgnoreAPInvoicesMatchingFiltersSchema =
  ListAPInvoicesInputSchema.pick({
    invoiceDateFrom: true,
    invoiceDateTo: true,
    dueDateFrom: true,
    dueDateTo: true,
    receivedAtFrom: true,
    receivedAtTo: true,
    status: true,
    paymentStatus: true,
    supplierId: true,
    supplierIds: true,
    invoiceNumber: true,
    referenceId: true,
    minTotalAmount: true,
    maxTotalAmount: true,
  });

function hasMatchingFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export const IgnoreAPInvoicesInputSchema = z
  .object({
    invoiceIds: z.array(UUIDSchema).min(1).max(1000).optional(),
    matchingFilters: IgnoreAPInvoicesMatchingFiltersSchema.optional(),
    includeFinalizedUnpaid: z.boolean().optional(),
    /**
     * Cancel open tasks anchored to the ignored invoices. Defaults to true.
     * Send false to leave those tasks open.
     */
    closeAssociatedTasks: z.boolean().optional(),
  })
  .refine(
    (value) => {
      const hasIds = (value.invoiceIds?.length ?? 0) > 0;
      const hasFilters =
        value.matchingFilters != null &&
        Object.values(value.matchingFilters).some(hasMatchingFilterValue);
      return hasIds !== hasFilters;
    },
    {
      error:
        "Provide either invoiceIds or matchingFilters with at least one filter",
    },
  );

export const IgnoreAPInvoicesResponseSchema = z.object({
  ignored: z.int().nonnegative(),
  failures: z.array(
    z.object({
      invoiceId: z.string(),
      error: z.string(),
    }),
  ),
});

export const UnignoreAPInvoicesInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1).max(1000),
  /**
   * Reopen tasks that were cancelled because the invoice was ignored.
   * Defaults to true. Tasks cancelled for any other reason stay cancelled.
   */
  reopenAssociatedTasks: z.boolean().optional(),
});

export const UnignoreAPInvoicesResponseSchema = z.object({
  unignored: z.int().nonnegative(),
  failures: z.array(
    z.object({
      invoiceId: z.string(),
      error: z.string(),
    }),
  ),
});

/**
 * Re-publish `AP_INVOICE_DRAFT_CREATED` for an existing invoice so every
 * workflow in the org subscribed to that event (e.g. "AP Invoice Processing":
 * match line items, Famous fetches, schedule payment) runs again from the
 * current invoice state. No email lineage needed — unlike a rerun of the
 * ingestion workflow, which resolves to the existing draft and never re-emits.
 * Only DRAFT / MATCHED invoices qualify.
 */
export const ReprocessAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const ReprocessAPInvoiceResponseSchema = z.object({
  success: z.literal(true),
});

/**
 * Re-run the exception evaluation — evaluation only, no re-matching, no email
 * lineage needed. Uses the rule set snapshotted on the invoice's latest
 * exception rows, or the org's only rule set when this invoice has never
 * been evaluated.
 */
export const ReevaluateAPInvoiceExceptionsInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1).max(100),
  /** When set, evaluate against this set instead of the invoice snapshot. */
  exceptionRuleSetId: UUIDSchema.optional(),
});

export const ReevaluateAPInvoiceExceptionsResultSchema = z.object({
  invoiceId: UUIDSchema,
  success: z.boolean(),
  error: z.string().optional(),
  /** Exceptions still open after the pass (success only). */
  openCount: z.int().nonnegative().optional(),
  openedCount: z.int().nonnegative().optional(),
  resolvedCount: z.int().nonnegative().optional(),
  ownerType: z.string().nullable().optional(),
});

export const ReevaluateAPInvoiceExceptionsResponseSchema = z.object({
  results: z.array(ReevaluateAPInvoiceExceptionsResultSchema),
});

/** One persisted exception row on an invoice, for the slide-in. */
export const APInvoiceExceptionViewSchema = z.object({
  id: UUIDSchema,
  status: z.enum(["open", "resolved"]),
  exceptionTypeOptionId: UUIDSchema,
  name: z.string(),
  reasoning: z.string().nullable(),
  ownerType: z.string().nullable(),
  ownerName: z.string().nullable(),
  detectedAt: z.string(),
  resolvedAt: z.string().nullable(),
  taskId: UUIDSchema.nullable(),
});

export const ListAPInvoiceExceptionsInputSchema = z.object({
  invoiceId: UUIDSchema,
});

export const ListAPInvoiceExceptionsResponseSchema = z.object({
  exceptions: z.array(APInvoiceExceptionViewSchema),
});

/**
 * Dry-run the exception evaluator against a rule set (saved and/or draft
 * guidance) on one or more invoices. Nothing is persisted.
 */
export const DraftExceptionGuidanceSchema = z.object({
  exceptionTypeOptionId: UUIDSchema,
  detectionGuidance: z.string().trim().max(2000).nullable(),
});

export const PreviewAPInvoiceExceptionsInputSchema = z
  .object({
    invoiceIds: z.array(UUIDSchema).min(1).max(8),
    /** Saved set to load vocabulary + owners from. */
    exceptionRuleSetId: UUIDSchema.optional(),
    /** Used when testing a set that has not been saved yet. */
    exceptionEnumId: UUIDSchema.optional(),
    /** Full draft rules (create flow). Replaces saved rules when provided. */
    rules: ApExceptionRulesSchema.optional(),
    /** Unsaved guidance only — applied on top of the saved / draft rules. */
    draftGuidance: z.array(DraftExceptionGuidanceSchema).max(100).optional(),
  })
  .refine(
    (input) =>
      input.exceptionRuleSetId !== undefined ||
      input.exceptionEnumId !== undefined,
    { message: "exceptionRuleSetId or exceptionEnumId is required" },
  );

export const ExceptionTestInvoiceReasonSchema = z.enum([
  "unmatched-open",
  "matched-control",
]);

export const SuggestedExceptionTestInvoiceSchema = z.object({
  invoiceId: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  status: z.string(),
  totalAmount: z.string().nullable(),
  currency: z.string(),
  whySelected: ExceptionTestInvoiceReasonSchema,
  matching: z.object({
    matchableLineCount: z.int().nonnegative(),
    matchedLineCount: z.int().nonnegative(),
    unmatchedLineCount: z.int().nonnegative(),
  }),
  factsText: z.string(),
});

export const SuggestExceptionTestInvoicesInputSchema = z.object({
  unmatchedLimit: z.int().min(1).max(6).optional(),
  controlLimit: z.int().min(0).max(4).optional(),
});

export const SuggestExceptionTestInvoicesResponseSchema = z.object({
  invoices: z.array(SuggestedExceptionTestInvoiceSchema),
});

export const PreviewedAPInvoiceExceptionSchema = z.object({
  exceptionTypeOptionId: UUIDSchema,
  name: z.string(),
  reasoning: z.string(),
  /** False when the type fired but the set has no rule for it (would open unassigned). */
  hasRule: z.boolean(),
});

export const PreviewedInvoiceExceptionsSchema = z.object({
  invoiceId: UUIDSchema,
  ruleSetId: UUIDSchema.nullable(),
  ruleSetName: z.string(),
  enumName: z.string(),
  /** Types the evaluator raised, in vocabulary order. */
  raised: z.array(PreviewedAPInvoiceExceptionSchema),
  /** Types in the vocabulary the evaluator did not raise. */
  notRaised: z.array(
    z.object({ exceptionTypeOptionId: UUIDSchema, name: z.string() }),
  ),
  /** The rule that would own the invoice (first by order among raised types). */
  owner: z
    .object({
      exceptionTypeOptionId: UUIDSchema,
      exceptionTypeName: z.string(),
      type: z.string(),
      id: UUIDSchema,
      /** Null when the referenced user / team / agent / workflow no longer exists. */
      name: z.string().nullable(),
    })
    .nullable(),
  /** True when a live pass would block auto-accept / Purchase Match Ready. */
  wouldBlock: z.boolean(),
  /** Ids the model returned that were not in the vocabulary (dropped). */
  invalidSelectionCount: z.int().nonnegative(),
  /** False when the vocabulary is empty and generateText is skipped. */
  llmCalled: z.boolean(),
  /** System instructions sent to the evaluator. */
  systemPrompt: z.string(),
  /** User prompt: exception vocabulary + invoice facts. */
  userPrompt: z.string(),
  /** The exact facts block the evaluator was shown. */
  factsText: z.string(),
});

export const PreviewAPInvoiceExceptionsResponseSchema = z.object({
  results: z.array(PreviewedInvoiceExceptionsSchema),
});

export const DeleteAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteAPInvoiceResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  deletedCounts: z
    .object({
      lineItems: z.int().nonnegative(),
      entityAssociations: z.int().nonnegative(),
      documentAssociations: z.int().nonnegative(),
      charges: z.int().nonnegative(),
      extractionResultsUnlinked: z.int().nonnegative(),
    })
    .optional(),
});

/**
 * Per-type structured summaries for supporting documents (AP-invoice-side
 * evidence: BOL, POD, Receiving Stamp, Carrier Confirmation). Discriminated on
 * `classificationType` so the frontend gets type-safe access to the fields
 * relevant for each doc type.
 *
 * The server fills missing fields with `null` rather than omitting them so
 * each summary has a stable shape per type. Free-form text fields are
 * sanitized server-side before being included.
 */
export const BillOfLadingSummarySchema = z.object({
  classificationType: z.literal("bill_of_lading"),
  bolNumber: z.string().nullable(),
  proNumber: z.string().nullable(),
  carrierName: z.string().nullable(),
  carrierScac: z.string().nullable(),
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  freightTerms: z.string().nullable(),
  poNumbers: z.array(z.string()),
  soNumbers: z.array(z.string()),
  totalPieces: z.number().nullable(),
  totalPallets: z.number().nullable(),
  totalWeight: z.number().nullable(),
  totalWeightUom: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
});

export const ProofOfDeliverySummarySchema = z.object({
  classificationType: z.literal("proof_of_delivery"),
  bolNumber: z.string().nullable(),
  proNumber: z.string().nullable(),
  carrierName: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  receivedByName: z.string().nullable(),
  signaturePresent: z.boolean().nullable(),
  poNumbers: z.array(z.string()),
  soNumbers: z.array(z.string()),
  lineItemCount: z.int().nonnegative(),
  exceptionCount: z.int().nonnegative(),
});

export const ReceivingStampSummarySchema = z.object({
  classificationType: z.literal("receiving_stamp"),
  underlyingDocumentType: z.string().nullable(),
  poNumber: z.string().nullable(),
  additionalPoNumbers: z.array(z.string()),
  bolNumber: z.string().nullable(),
  packingSlipNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  receivedDate: z.string().nullable(),
  receivedBy: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
  exceptionCount: z.int().nonnegative(),
});

export const CarrierConfirmationSummarySchema = z.object({
  classificationType: z.literal("carrier_confirmation"),
  confirmationNumber: z.string().nullable(),
  bolNumber: z.string().nullable(),
  proNumber: z.string().nullable(),
  carrierName: z.string().nullable(),
  carrierScac: z.string().nullable(),
  carrierMcNumber: z.string().nullable(),
  brokerName: z.string().nullable(),
  equipmentType: z.string().nullable(),
  agreedRate: z.number().nullable(),
  rateCurrency: z.string().nullable(),
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  poNumbers: z.array(z.string()),
  soNumbers: z.array(z.string()),
  rateBreakdownItemCount: z.int().nonnegative(),
});

export const ReceiptSummarySchema = z.object({
  classificationType: z.literal("receipt"),
  vendorName: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  receiptDate: z.string().nullable(),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  poNumbers: z.array(z.string()),
  invoiceNumbers: z.array(z.string()),
  referenceNumbers: z.array(z.string()),
  lineItemCount: z.int().nonnegative(),
});

export const SupportingDocSummarySchema = z.discriminatedUnion(
  "classificationType",
  [
    BillOfLadingSummarySchema,
    ProofOfDeliverySummarySchema,
    ReceivingStampSummarySchema,
    CarrierConfirmationSummarySchema,
    ReceiptSummarySchema,
  ],
);

export const SupportingDocClassificationSchema = z.enum([
  "bill_of_lading",
  "proof_of_delivery",
  "receiving_stamp",
  "carrier_confirmation",
  "receipt",
]);

export const SupportingDocOcrSchema = z.object({
  id: UUIDSchema,
  status: z.enum(["pending", "processing", "completed", "failed"]),
  vendor: z.string(),
  modelVersion: z.string(),
  error: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export const SupportingDocItemSchema = z.object({
  documentId: UUIDSchema,
  classificationType: SupportingDocClassificationSchema,
  originalFileName: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.int().nonnegative().nullable(),
  classifiedAt: z.coerce.date().nullable(),
  classificationConfidence: z.number().nullable(),
  associatedAt: z.coerce.date(),
  /**
   * Latest OCR result metadata. `null` if no OCR has been attempted for the
   * document yet. Use `core.documents.getOcrResultContent` with `id` to fetch
   * the full extracted JSON when the user expands the raw view.
   */
  ocr: SupportingDocOcrSchema.nullable(),
  /**
   * Server-computed structured digest of the OCR extraction. `null` when:
   *   - There is no OCR result yet, or
   *   - OCR is not in `completed` status, or
   *   - The extraction shape did not match the expected schema for this type
   *     (defensive — avoids crashing on partial / malformed extractions).
   */
  summary: SupportingDocSummarySchema.nullable(),
});

export const ListAPInvoiceSupportingDocumentsInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const ListAPInvoiceSupportingDocumentsResponseSchema = z.object({
  documents: z.array(SupportingDocItemSchema),
});

export const SnoozeAPInvoicesInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1).max(100),
  until: z.coerce.date(),
  untilLocalDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  preset: z.enum(AP_INVOICE_SNOOZE_PRESETS),
  note: z.string().trim().max(2000).optional(),
  clearsOnEdit: z.boolean().prefault(true),
  confirmTasks: z.boolean().prefault(false),
  confirmPastDue: z.boolean().prefault(false),
});

export const SnoozeAPInvoicesResponseSchema = z.object({
  invoices: z.array(APInvoiceSchema),
  skipped: z.array(
    z.object({
      invoiceId: UUIDSchema,
      reason: z.enum(["not_found", "not_snoozable", "not_snoozed"]),
    }),
  ),
});

export const ClearAPInvoiceSnoozesInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1).max(100),
});

export const ListAPInvoiceHoldsInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const ListAPInvoiceHoldsResponseSchema = z.object({
  holds: z.array(APInvoiceHoldSchema),
});

/** The kind of Famous file a voucher line was allocated against. */
export const FamousVoucherFileTypeSchema = z
  .enum(["load", "order", "po", "other"])
  .describe(
    "Famous file the voucher line was matched against: load (sales load charge), order (sales order charge), po (purchase order charge) or other.",
  );

/** One Famous file (load, order or PO) by its Famous number. */
export const FamousVoucherFileRefSchema = z.object({
  fileType: FamousVoucherFileTypeSchema,
  fileNumber: z.string(),
});

export const FamousVoucherLineSchema = z.object({
  lineSeq: z.int().nullable(),
  fileType: FamousVoucherFileTypeSchema,
  /** Famous load, order or PO number the line was allocated against. */
  fileNumber: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number().nullable(),
  /** Signed: offsetting lines (an order's pick-up credit) are negative. */
  amount: DecimalStringSchema,
});

export const FamousVoucherFileSchema = FamousVoucherFileRefSchema.extend({
  lineCount: z.int(),
  subtotal: DecimalStringSchema,
});

export const FamousVoucherCoverageSchema = z.object({
  /** Files with at least one line on this voucher. */
  covered: z.array(FamousVoucherFileRefSchema),
  /**
   * Files the invoice is linked to in Gently, or that another voucher for the
   * invoice covers, that this voucher has no line for.
   */
  missing: z.array(FamousVoucherFileRefSchema),
  /** Files on this voucher that the invoice is not linked to in Gently. */
  notLinked: z.array(FamousVoucherFileRefSchema),
  complete: z.boolean(),
});

export const FamousVoucherSchema = z.object({
  voucherNumber: z.int(),
  invoiceNumber: z.string(),
  invoiceDate: z.string().nullable(),
  amount: DecimalStringSchema,
  paidAmount: DecimalStringSchema,
  stubDescription: z.string().nullable(),
  /** Famous `AP_STATUS` code, unmapped. */
  apStatus: z.string().nullable(),
  onHold: z.boolean(),
  /** `unvouchered` = $0 (voided), `paid` = fully paid, otherwise `open`. */
  state: z.enum(["unvouchered", "open", "paid"]),
  /** The newest voucher with an amount: the one that counts for the invoice. */
  isCurrent: z.boolean(),
  /** The voucher amount differs from the invoice total (non-$0 vouchers only). */
  amountDiffersFromInvoice: z.boolean(),
  lines: z.array(FamousVoucherLineSchema),
  files: z.array(FamousVoucherFileSchema),
  /** Null for $0 vouchers, which cover nothing. */
  coverage: FamousVoucherCoverageSchema.nullable(),
});

export const ListAPInvoiceFamousVouchersResponseSchema = z.object({
  /**
   * `ok`: vouchers read from Famous (possibly none). `unavailable`: Famous
   * could not be read right now. `not_applicable`: the organization or the
   * supplier is not linked to Famous.
   */
  status: z.enum(["ok", "unavailable", "not_applicable"]),
  reason: z.string().nullable(),
  /** Invoice-number spellings searched (e.g. 18980A and 18980). */
  searchedInvoiceNumbers: z.array(z.string()),
  invoiceTotal: DecimalStringSchema.nullable(),
  /** Files the invoice is linked to in Gently. */
  linkedFiles: z.array(FamousVoucherFileRefSchema),
  /** Newest first. */
  vouchers: z.array(FamousVoucherSchema),
});

export const apInvoicesContracts = {
  list: oc
    .input(ListAPInvoicesInputSchema)
    .output(ListAPInvoicesResponseSchema)
    .route({
      method: "GET",
      summary: "List AP invoices",
      description:
        "Lists supplier (AP) invoices filterable by invoice, due, or received date, status, payment status, supplier, invoice number, reference, amount range, or IDs, with sorting, paging, and optional snooze hiding.",
    }),
  get: oc
    .input(GetAPInvoiceInputSchema)
    .output(GetAPInvoiceResponseSchema)
    .route({
      method: "GET",
      summary: "Get an AP invoice",
      description:
        "Returns one supplier invoice with line items, the source email, related conversations, and a warning when line totals differ from the extracted document total.",
    }),
  getExtractionResult: oc
    .input(GetAPInvoiceExtractionResultInputSchema)
    .output(GetAPInvoiceExtractionResultResponseSchema)
    .route({
      method: "GET",
      summary: "Get an AP invoice's extraction result",
      description:
        "Returns the AI document-extraction result behind a supplier invoice, including its status and extracted fields, or null if none.",
    }),
  update: oc
    .input(UpdateAPInvoiceInputSchema)
    .output(UpdateAPInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Update an AP invoice",
      description:
        "Edits a supplier invoice's number, supplier, reference IDs, invoice date, due date, preferred pay date, or scheduled payment date and explanation.",
    }),
  createVoucherInFamous: oc
    .input(CreateVoucherInFamousInputSchema)
    .output(CreateVoucherInFamousResponseSchema)
    .route({
      method: "POST",
      summary: "Create an invoice voucher in the ERP",
      description:
        "Posts a draft or matched supplier invoice to the ERP as a voucher. Needs line items and a supplier with an ERP vendor ID. Returns the voucher ID or error.",
    }),
  acceptMatches: oc
    .input(AcceptMatchesAPInvoiceInputSchema)
    .output(AcceptMatchesAPInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Accept an AP invoice's matches",
      description:
        "Accepts a draft or matched supplier invoice's line matches, marking it accepted and approved by the user. Requires supplier and invoice number; starts downstream accepted-invoice workflows.",
    }),
  unacceptMatches: oc
    .input(UnacceptMatchesAPInvoiceInputSchema)
    .output(UnacceptMatchesAPInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Revert an accepted AP invoice to draft",
      description:
        "Moves an accepted supplier invoice back to draft and clears its approval, keeping existing line matches for re-review.",
    }),
  createPayment: oc
    .input(CreatePaymentAPInvoiceInputSchema)
    .output(CreatePaymentAPInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Add an accepted invoice to a payment",
      description:
        "Adds an accepted supplier invoice to the supplier's in-review payment for its payment date, creating the payment if needed. Does not send money.",
    }),
  markPaid: oc
    .input(MarkAPInvoicesPaidInputSchema)
    .output(MarkAPInvoicesPaidResponseSchema)
    .route({
      method: "POST",
      summary: "Mark AP invoices as paid externally",
      description:
        "Records up to 1000 supplier invoices as paid outside the system on a date, with method and optional confirmation number, creating a paid payment for each. Moves no money.",
    }),
  lookup: oc
    .input(LookupAPInvoicesInputSchema)
    .output(LookupAPInvoicesResponseSchema)
    .route({
      method: "POST",
      summary: "Look up AP invoices by number or ID",
      description:
        "Resolves up to 500 pasted invoice numbers (exact, case-insensitive) or invoice IDs to supplier invoices, one result per identifier. Read only.",
    }),
  parseCheckRunReport: oc
    .input(ParseCheckRunReportInputSchema)
    .output(ParseCheckRunReportResponseSchema)
    .route({
      method: "POST",
      summary: "Read a Famous check run report",
      description:
        "Reads a Famous 'UnPaid Voucher Check Run' PDF and matches each voucher row to a supplier invoice by voucher number, else invoice number and vendor. Read only.",
    }),
  bulkCreatePayments: oc
    .input(BulkCreatePaymentsInputSchema)
    .output(BulkCreatePaymentsResponseSchema)
    .route({
      method: "POST",
      summary: "Create payments for multiple accepted invoices",
      description:
        "Groups accepted supplier invoices by supplier and payment date into in-review payments, with optional per-invoice date overrides. Returns counts and per-invoice failures. Does not send money.",
    }),
  ignore: oc
    .input(IgnoreAPInvoicesInputSchema)
    .output(IgnoreAPInvoicesResponseSchema)
    .route({
      method: "POST",
      summary: "Ignore AP invoices",
      description:
        "Marks supplier invoices as ignored, by ID list or list filters, and by default cancels their open tasks. Can include finalized unpaid invoices.",
    }),
  snooze: oc
    .input(SnoozeAPInvoicesInputSchema)
    .output(SnoozeAPInvoicesResponseSchema)
    .route({
      method: "POST",
      summary: "Snooze AP invoices",
      description:
        "Hides draft supplier invoices from the invoice list until a future time, with an optional note. Status, tasks, and workflows are unchanged; asks to confirm open tasks or past-due snoozes.",
    }),
  clearSnooze: oc
    .input(ClearAPInvoiceSnoozesInputSchema)
    .output(SnoozeAPInvoicesResponseSchema)
    .route({
      method: "POST",
      summary: "Clear snooze on AP invoices",
      description:
        "Removes the active snooze from up to 100 supplier invoices so they reappear in the invoice list.",
    }),
  unignore: oc
    .input(UnignoreAPInvoicesInputSchema)
    .output(UnignoreAPInvoicesResponseSchema)
    .route({
      method: "POST",
      summary: "Restore ignored AP invoices",
      description:
        "Un-ignores supplier invoices and, by default, reopens tasks that were cancelled when they were ignored.",
    }),
  reprocess: oc
    .input(ReprocessAPInvoiceInputSchema)
    .output(ReprocessAPInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Reprocess an AP invoice",
      description:
        "Re-runs the organization's invoice-processing workflows (line matching, ERP lookups, payment scheduling) on a draft or matched supplier invoice from its current state.",
    }),
  reevaluateExceptions: oc
    .input(ReevaluateAPInvoiceExceptionsInputSchema)
    .output(ReevaluateAPInvoiceExceptionsResponseSchema)
    .route({
      method: "POST",
      summary: "Re-check AP invoices for exceptions",
      description:
        "Re-runs AI exception evaluation on up to 100 supplier invoices without re-matching, using their rule set or a chosen one. Opens or resolves exceptions and routes them to owners.",
    }),
  previewExceptions: oc
    .input(PreviewAPInvoiceExceptionsInputSchema)
    .output(PreviewAPInvoiceExceptionsResponseSchema)
    .route({
      method: "POST",
      summary: "Test exception rules on AP invoices",
      description:
        "Dry-runs AI exception evaluation on up to 8 invoices with a saved or draft rule set, returning raised exceptions, owner, whether it would block, and prompts. Saves nothing.",
    }),
  suggestExceptionTestInvoices: oc
    .input(SuggestExceptionTestInvoicesInputSchema)
    .output(SuggestExceptionTestInvoicesResponseSchema)
    .route({
      method: "POST",
      summary: "Suggest invoices for testing exception rules",
      description:
        "Picks sample supplier invoices for testing exception rules: unmatched open ones plus matched controls, with matching counts and the facts the evaluator sees.",
    }),
  listExceptions: oc
    .input(ListAPInvoiceExceptionsInputSchema)
    .output(ListAPInvoiceExceptionsResponseSchema)
    .route({
      method: "GET",
      summary: "List exceptions on an AP invoice",
      description:
        "Lists open and resolved exceptions on a supplier invoice with type, reasoning, owner, detection and resolution times, and linked task.",
    }),
  delete: oc
    .input(DeleteAPInvoiceInputSchema)
    .output(DeleteAPInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Delete an AP invoice",
      description:
        "Deletes a supplier invoice with its line items, charges, and document links, and unlinks its extraction result. Finalized or void invoices cannot be deleted.",
    }),
  updateLineItem: oc
    .input(UpdateAPInvoiceLineItemInputSchema)
    .output(UpdateAPInvoiceLineItemResponseSchema)
    .route({
      method: "POST",
      summary: "Update an AP invoice line",
      description:
        "Changes quantity or unit price on a line of a draft or matched supplier invoice and recalculates the invoice totals.",
    }),
  splitLineItem: oc
    .input(SplitAPInvoiceLineItemInputSchema)
    .output(SplitAPInvoiceLineItemResponseSchema)
    .route({
      method: "POST",
      summary: "Split an AP invoice line into separately matchable parts",
      description:
        "Replaces one line on a draft or matched invoice with 2–20 parts whose totals add up to the original line, so each part can be matched to its own PO/SO line or charge. Removes any match on the split line.",
    }),
  undoLineItemSplit: oc
    .input(UndoAPInvoiceLineItemSplitInputSchema)
    .output(UndoAPInvoiceLineItemSplitResponseSchema)
    .route({
      method: "POST",
      summary: "Undo the split of an AP invoice line",
      description:
        "Collapses every part of a split line on a draft or matched invoice back into the original line, with its original quantity and amount, and removes the parts' matches. Returns the invoice's lines.",
    }),
  aggregateLineItems: oc
    .input(AggregateAPInvoiceLineItemsInputSchema)
    .output(AggregateAPInvoiceLineItemsResponseSchema)
    .route({
      method: "POST",
      summary: "Aggregate AP invoice lines into one match",
      description:
        "Matches 2–20 lines of a draft or matched invoice to a single PO/SO/TO line or charge as one aggregated match. Replaces any existing match on those lines. Refuses lines already in an aggregated match and a target that another line of the invoice is matched to.",
    }),
  acceptLineSplitProposal: oc
    .input(LineSplitProposalActionInputSchema)
    .output(LineSplitProposalActionResponseSchema)
    .route({
      method: "POST",
      summary: "Accept a suggested split of an AP invoice line",
      description:
        "Confirms the tentative matches a split suggestion created for one line of a draft or matched invoice, after checking that every charge is still current and still adds up to the line. A stale suggestion is removed and the request fails.",
    }),
  dismissLineSplitProposal: oc
    .input(LineSplitProposalActionInputSchema)
    .output(LineSplitProposalActionResponseSchema)
    .route({
      method: "POST",
      summary: "Dismiss a suggested split of an AP invoice line",
      description:
        "Removes the tentative matches of a split suggestion on one line of a draft or matched invoice and remembers the dismissal, so the same suggestion is not made again.",
    }),
  listChangeLog: oc
    .input(ListAPInvoiceChangeLogInputSchema)
    .output(ListAPInvoiceChangeLogResponseSchema)
    .route({
      method: "GET",
      summary: "List an AP invoice's change history",
      description:
        "Returns a supplier invoice's history: field and line changes, match changes, and workflow runs, with who changed what and when.",
    }),
  listSupportingDocuments: oc
    .input(ListAPInvoiceSupportingDocumentsInputSchema)
    .output(ListAPInvoiceSupportingDocumentsResponseSchema)
    .route({
      method: "GET",
      summary: "List an AP invoice's supporting documents",
      description:
        "Lists supporting documents attached to a supplier invoice, such as bills of lading, proofs of delivery, receiving stamps, and carrier confirmations, with key extracted fields.",
    }),
  /**
   * Every source-ERP hold on the POs, sales orders and loads linked to the
   * invoice, matched or not.
   */
  listHolds: oc
    .input(ListAPInvoiceHoldsInputSchema)
    .output(ListAPInvoiceHoldsResponseSchema)
    .route({
      method: "GET",
      summary: "List ERP holds affecting an AP invoice",
      description:
        "Returns every ERP hold on the purchase orders, sales orders and loads linked to a supplier invoice, whether or not the lines are matched.",
    }),
  /**
   * Live, read-only lookup of the Famous vouchers for the invoice. Nothing is
   * stored; Famous being unreachable is a status, not an error.
   */
  listFamousVouchers: oc
    .input(ListAPInvoiceHoldsInputSchema)
    .output(ListAPInvoiceFamousVouchersResponseSchema)
    .route({
      method: "GET",
      summary: "List the Famous vouchers for an AP invoice",
      description:
        "Reads the Famous AP vouchers for a supplier invoice (both invoice-number spellings, matched on the supplier's Famous vendor id), newest first, with each voucher's lines grouped by load, order and PO and whether it covers every file the invoice is linked to. Read-only; returns status unavailable when Famous cannot be reached.",
    }),
} as const;

export type APInvoice = z.infer<typeof APInvoiceSchema>;
export type APInvoiceHold = z.infer<typeof APInvoiceHoldSchema>;
export type FamousVoucherFileType = z.infer<typeof FamousVoucherFileTypeSchema>;
export type FamousVoucherFileRef = z.infer<typeof FamousVoucherFileRefSchema>;
export type FamousVoucherLine = z.infer<typeof FamousVoucherLineSchema>;
export type FamousVoucherFile = z.infer<typeof FamousVoucherFileSchema>;
export type FamousVoucherCoverage = z.infer<typeof FamousVoucherCoverageSchema>;
export type FamousVoucher = z.infer<typeof FamousVoucherSchema>;
export type ListAPInvoiceFamousVouchersResponse = z.infer<
  typeof ListAPInvoiceFamousVouchersResponseSchema
>;
export type APInvoiceHoldOrigin = z.infer<typeof APInvoiceHoldOriginSchema>;
export type APInvoiceLineItem = z.infer<typeof APInvoiceLineItemSchema>;
export type APInvoiceLineItemSplit = z.infer<
  typeof APInvoiceLineItemSplitSchema
>;
export type APInvoiceLineItemSplitProposal = z.infer<
  typeof APInvoiceLineItemSplitProposalSchema
>;
export type APInvoiceLineItemSplitProposalAllocation = z.infer<
  typeof APInvoiceLineItemSplitProposalAllocationSchema
>;
export type InboundEmail = z.infer<typeof InboundEmailSchema>;
export type ListAPInvoicesInput = z.infer<typeof ListAPInvoicesInputSchema>;
export type ListAPInvoicesResponse = z.infer<
  typeof ListAPInvoicesResponseSchema
>;
export type GetAPInvoiceInput = z.infer<typeof GetAPInvoiceInputSchema>;
export type GetAPInvoiceResponse = z.infer<typeof GetAPInvoiceResponseSchema>;
export type APInvoiceRelatedConversation = z.infer<
  typeof APInvoiceRelatedConversationSchema
>;
export type GetAPInvoiceExtractionResultInput = z.infer<
  typeof GetAPInvoiceExtractionResultInputSchema
>;
export type APInvoiceExtractionResult = z.infer<
  typeof APInvoiceExtractionResultSchema
>;
export type GetAPInvoiceExtractionResultResponse = z.infer<
  typeof GetAPInvoiceExtractionResultResponseSchema
>;
export type CreateVoucherInFamousInput = z.infer<
  typeof CreateVoucherInFamousInputSchema
>;
export type CreateVoucherInFamousResponse = z.infer<
  typeof CreateVoucherInFamousResponseSchema
>;
export type UpdateAPInvoiceInput = z.infer<typeof UpdateAPInvoiceInputSchema>;
export type UpdateAPInvoiceResponse = z.infer<
  typeof UpdateAPInvoiceResponseSchema
>;
export type UpdateAPInvoiceLineItemInput = z.infer<
  typeof UpdateAPInvoiceLineItemInputSchema
>;
export type UpdateAPInvoiceLineItemResponse = z.infer<
  typeof UpdateAPInvoiceLineItemResponseSchema
>;
export type SplitAPInvoiceLineItemPart = z.infer<
  typeof SplitAPInvoiceLineItemPartSchema
>;
export type SplitAPInvoiceLineItemInput = z.infer<
  typeof SplitAPInvoiceLineItemInputSchema
>;
export type SplitAPInvoiceLineItemResponse = z.infer<
  typeof SplitAPInvoiceLineItemResponseSchema
>;
export type UndoAPInvoiceLineItemSplitInput = z.infer<
  typeof UndoAPInvoiceLineItemSplitInputSchema
>;
export type AggregateAPInvoiceLineItemsInput = z.infer<
  typeof AggregateAPInvoiceLineItemsInputSchema
>;
export type AggregateAPInvoiceLineItemsResponse = z.infer<
  typeof AggregateAPInvoiceLineItemsResponseSchema
>;
export type AcceptMatchesAPInvoiceInput = z.infer<
  typeof AcceptMatchesAPInvoiceInputSchema
>;
export type AcceptMatchesAPInvoiceResponse = z.infer<
  typeof AcceptMatchesAPInvoiceResponseSchema
>;
export type UnacceptMatchesAPInvoiceInput = z.infer<
  typeof UnacceptMatchesAPInvoiceInputSchema
>;
export type UnacceptMatchesAPInvoiceResponse = z.infer<
  typeof UnacceptMatchesAPInvoiceResponseSchema
>;
export type CreatePaymentAPInvoiceInput = z.infer<
  typeof CreatePaymentAPInvoiceInputSchema
>;
export type CreatePaymentAPInvoiceResponse = z.infer<
  typeof CreatePaymentAPInvoiceResponseSchema
>;
export type ReprocessAPInvoiceInput = z.infer<
  typeof ReprocessAPInvoiceInputSchema
>;
export type ReprocessAPInvoiceResponse = z.infer<
  typeof ReprocessAPInvoiceResponseSchema
>;
export type LookupAPInvoicesInput = z.infer<typeof LookupAPInvoicesInputSchema>;
export type LookupAPInvoicesResponse = z.infer<
  typeof LookupAPInvoicesResponseSchema
>;
export type CheckRunReportLine = z.infer<typeof CheckRunReportLineSchema>;
export type CheckRunMatchMethod = z.infer<typeof CheckRunMatchMethodSchema>;
export type ParseCheckRunReportResponse = z.infer<
  typeof ParseCheckRunReportResponseSchema
>;
export type MarkAPInvoicePaidMethod = z.infer<
  typeof MarkAPInvoicePaidMethodSchema
>;
export type MarkAPInvoicesPaidInput = z.infer<
  typeof MarkAPInvoicesPaidInputSchema
>;
export type MarkAPInvoicesPaidResponse = z.infer<
  typeof MarkAPInvoicesPaidResponseSchema
>;
export type IgnoreAPInvoicesMatchingFilters = z.infer<
  typeof IgnoreAPInvoicesMatchingFiltersSchema
>;
export type IgnoreAPInvoicesInput = z.infer<typeof IgnoreAPInvoicesInputSchema>;
export type IgnoreAPInvoicesResponse = z.infer<
  typeof IgnoreAPInvoicesResponseSchema
>;
export type UnignoreAPInvoicesInput = z.infer<
  typeof UnignoreAPInvoicesInputSchema
>;
export type UnignoreAPInvoicesResponse = z.infer<
  typeof UnignoreAPInvoicesResponseSchema
>;
export type ReevaluateAPInvoiceExceptionsInput = z.infer<
  typeof ReevaluateAPInvoiceExceptionsInputSchema
>;
export type ReevaluateAPInvoiceExceptionsResult = z.infer<
  typeof ReevaluateAPInvoiceExceptionsResultSchema
>;
export type ReevaluateAPInvoiceExceptionsResponse = z.infer<
  typeof ReevaluateAPInvoiceExceptionsResponseSchema
>;
export type PreviewAPInvoiceExceptionsInput = z.infer<
  typeof PreviewAPInvoiceExceptionsInputSchema
>;
export type PreviewedAPInvoiceException = z.infer<
  typeof PreviewedAPInvoiceExceptionSchema
>;
export type PreviewedInvoiceExceptions = z.infer<
  typeof PreviewedInvoiceExceptionsSchema
>;
export type PreviewAPInvoiceExceptionsResponse = z.infer<
  typeof PreviewAPInvoiceExceptionsResponseSchema
>;
export type SuggestedExceptionTestInvoice = z.infer<
  typeof SuggestedExceptionTestInvoiceSchema
>;
export type SuggestExceptionTestInvoicesInput = z.infer<
  typeof SuggestExceptionTestInvoicesInputSchema
>;
export type SuggestExceptionTestInvoicesResponse = z.infer<
  typeof SuggestExceptionTestInvoicesResponseSchema
>;
export type APInvoiceExceptionView = z.infer<
  typeof APInvoiceExceptionViewSchema
>;
export type ListAPInvoiceExceptionsInput = z.infer<
  typeof ListAPInvoiceExceptionsInputSchema
>;
export type ListAPInvoiceExceptionsResponse = z.infer<
  typeof ListAPInvoiceExceptionsResponseSchema
>;
export type DeleteAPInvoiceInput = z.infer<typeof DeleteAPInvoiceInputSchema>;
export type DeleteAPInvoiceResponse = z.infer<
  typeof DeleteAPInvoiceResponseSchema
>;
export type InvoiceChangeLogEntry = z.infer<typeof InvoiceChangeLogEntrySchema>;
export type ListAPInvoiceChangeLogInput = z.infer<
  typeof ListAPInvoiceChangeLogInputSchema
>;
export type ListAPInvoiceChangeLogResponse = z.infer<
  typeof ListAPInvoiceChangeLogResponseSchema
>;

export type SupportingDocClassification = z.infer<
  typeof SupportingDocClassificationSchema
>;
export type BillOfLadingSummary = z.infer<typeof BillOfLadingSummarySchema>;
export type ProofOfDeliverySummary = z.infer<
  typeof ProofOfDeliverySummarySchema
>;
export type ReceivingStampSummary = z.infer<typeof ReceivingStampSummarySchema>;
export type CarrierConfirmationSummary = z.infer<
  typeof CarrierConfirmationSummarySchema
>;
export type ReceiptSummary = z.infer<typeof ReceiptSummarySchema>;
export type SupportingDocSummary = z.infer<typeof SupportingDocSummarySchema>;
export type SupportingDocOcr = z.infer<typeof SupportingDocOcrSchema>;
export type SupportingDocItem = z.infer<typeof SupportingDocItemSchema>;
export type ListAPInvoiceSupportingDocumentsInput = z.infer<
  typeof ListAPInvoiceSupportingDocumentsInputSchema
>;
export type ListAPInvoiceSupportingDocumentsResponse = z.infer<
  typeof ListAPInvoiceSupportingDocumentsResponseSchema
>;
